import React, { useCallback, useMemo, useState } from 'react';

import { Edit, Power, RefreshCw } from 'lucide-react';
import type { ColDef } from 'ag-grid-community';

import { useRoleSwitcher } from '../../../hooks/useRoleSwitcher';
import {
  IconButton,
  UniversalModuleView,
} from '../../../ui-components/listing';
import StatusTag from '../../../ui-components/StatusTag';
import { API_CONSTANTS } from '../../../utils/constants';

import PushNotificationEditModal from './components/PushNotificationEditModal';
import { useEditPushNotificationMutation } from './pushNotificationsApi';
import type { PushNotificationRecord } from './model';

const SUPER_ADMIN_ROLE_CODES = ['HBADMIN', 'SUPER_ADMIN', 'SUPERADMIN'];

const formatDate = (value?: string) => {
  if (!value) return '—';
  try {
    const date = new Date(value);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const PushNotificationsPage: React.FC = () => {
  const [refreshToken, setRefreshToken] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null
  );
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const { activeRoleCode } = useRoleSwitcher();
  const canManageStatus = SUPER_ADMIN_ROLE_CODES.includes(
    (activeRoleCode || '').trim().toUpperCase()
  );
  const [editPushNotification] = useEditPushNotificationMutation();

  const openEditModal = useCallback((templateId: string) => {
    setEditingTemplateId(templateId);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingTemplateId(null);
  }, []);

  const handleRefreshSuccess = useCallback(() => {
    setRefreshToken((prev) => prev + 1);
  }, []);

  const handleStatusToggle = useCallback(
    async (row: PushNotificationRecord) => {
      const currentStatus = (row.status || '').trim().toLowerCase();
      const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';

      try {
        setUpdatingStatusId(row.id);
        await editPushNotification({
          id: row.id,
          status: nextStatus,
        }).unwrap();
        setRefreshToken((prev) => prev + 1);
      } catch {
        // Toast is already handled globally by baseQueryWithReauth.
      } finally {
        setUpdatingStatusId(null);
      }
    },
    [editPushNotification]
  );

  const columns = useMemo<ColDef<PushNotificationRecord>[]>(
    () => {
      const baseColumns: ColDef<PushNotificationRecord>[] = [
        {
          headerName: 'Template ID',
          field: 'templateId',
          headerClass: 'font-semibold',
          minWidth: 150,
          sortable: true,
        },
        {
          headerName: 'Template Name',
          field: 'templateName',
          minWidth: 220,
          sortable: false,
          cellClass: 'font-medium',
          cellRenderer: ({ data }) => {
            return (
              <button
                type="button"
                className="text-left text-brand-600 cursor-pointer truncate hover:underline"
                onClick={() => data && openEditModal(data.id)}
              >
                {data?.templateName ?? '—'}
              </button>
            );
          },
        },
        {
          headerName: 'Last Updated',
          field: 'lastUpdated',
          minWidth: 160,
          sortable: true,
          valueFormatter: (params: any) => formatDate(params.value),
        },
      ];

      if (canManageStatus) {
        baseColumns.push({
          headerName: 'Status',
          field: 'status',
          minWidth: 120,
          sortable: false,
          cellRenderer: ({ value }) => (
            <StatusTag value={typeof value === 'string' ? value : undefined} />
          ),
        });
      }

      baseColumns.push({
        headerName: 'Action',
        field: '__actions',
        sortable: false,
        maxWidth: canManageStatus ? 170 : 120,
        headerClass: 'text-right',
        cellClass: 'justify-end text-right',
        cellRenderer: ({ data }) => {
          if (!data) return null;

          const statusLabel = (data.status || '').trim().toLowerCase();
          const isProcessing = updatingStatusId === data.id;
          const toggleTitle =
            statusLabel === 'active'
              ? 'Inactivate push notification'
              : 'Activate push notification';

          return (
            <div className="flex items-center justify-end gap-2">
              {canManageStatus ? (
                <IconButton
                  icon={Power}
                  title={toggleTitle}
                  disabled={isProcessing}
                  onClick={() => handleStatusToggle(data)}
                />
              ) : null}
              <IconButton
                icon={Edit}
                title="Edit push notification"
                onClick={() => openEditModal(data.id)}
              />
            </div>
          );
        },
      });

      return baseColumns;
    },
    [canManageStatus, handleStatusToggle, openEditModal, updatingStatusId]
  );

  return (
    <>
      <UniversalModuleView<PushNotificationRecord>
        moduleKey="push-notifications"
        title="Push Notifications"
        apiEndpoint={API_CONSTANTS.PUSH_NOTIFICATIONS.LIST_URL}
        listMethod="GET"
        columnsConfig={columns}
        rowKey={(row) => row.id}
        refreshToken={refreshToken}
        extraActions={({ reset }) => (
          <IconButton
            icon={RefreshCw}
            title="Refresh push notification list"
            onClick={() => {
              reset();
              setRefreshToken((prev) => prev + 1);
            }}
          />
        )}
        hideFilterButton
        searchPlaceholder="Search templates or codes..."
      />

      <PushNotificationEditModal
        isOpen={isModalOpen}
        notificationTemplateId={editingTemplateId ?? undefined}
        onClose={closeModal}
        onSuccess={handleRefreshSuccess}
      />
    </>
  );
};

export default PushNotificationsPage;
